import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const TermsOfService = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <div className="terms-page">
      {/* Header Navigation */}
      <header className={`landing-header ${isVisible ? 'header-visible' : ''}`}>
        <div className="header-content">
          <div className={`logo ${isVisible ? 'logo-visible' : ''}`} onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span className="logo-text">UniPostings</span>
          </div>
          <nav className={`nav-menu ${isVisible ? 'nav-visible' : ''}`}>
            <Button 
              label="Back to Home" 
              className="header-cta"
              onClick={() => navigate('/')}
            />
          </nav>
        </div>
      </header>

      {/* Terms of Service Content */}
      <div className={`terms-content ${isVisible ? 'content-visible' : ''}`}>
        <div className="terms-container">
          <h1 className="terms-title">Terms of Service</h1>
          
          <div className="terms-meta">
            <p><strong>Effective Date:</strong> November 11, 2025</p>
            <p><strong>Last Updated:</strong> November 11, 2025</p>
          </div>

          <div className="terms-intro">
            <p>
              Welcome to UniPostings ("we," "our," or "us"). By accessing or using unipostings.com, our submission forms, or any of our affiliated social media accounts (the "Service"), you agree to be bound by these Terms of Service ("Terms"). Please read them carefully before using UniPostings.
            </p>
            <p className="terms-warning">
              If you do not agree to these Terms, please do not use our Service.
            </p>
          </div>

          {/* Section 1 */}
          <section className="terms-section">
            <h2>1. Overview</h2>
            <p>
              UniPostings is a community platform that helps incoming college freshmen connect with one another through bio posts on school-specific social media accounts and related digital platforms.
            </p>
            <p>
              By submitting content to UniPostings or accessing our site, you agree that you are at least 17 years old and authorized to share the information you provide.
            </p>
          </section>

          {/* Section 2 */}
          <section className="terms-section">
            <h2>2. User Content and Submissions</h2>
            <p>When you submit your information (bio, photo, social media handle, etc.) to UniPostings:</p>
            <ul>
              <li>
                You grant UniPostings a non-exclusive, royalty-free, worldwide license to use, display, repost, reproduce, modify, and distribute your content for the purpose of operating and promoting the Service.
              </li>
              <li>
                You represent and warrant that:
                <ul className="nested-list">
                  <li>You own or have permission to share the content you submit.</li>
                  <li>Your submission does not infringe on the rights of any third party.</li>
                  <li>Your content is not unlawful, defamatory, obscene, or otherwise inappropriate.</li>
                </ul>
              </li>
            </ul>
            <p className="terms-note">
              You may request removal of your post at any time by contacting{' '}
              <a href="mailto:hello@unipostings.com" className="terms-link">hello@unipostings.com</a>.
            </p>
          </section>

          {/* Section 3 */}
          <section className="terms-section">
            <h2>3. Account and Access</h2>
            <p>You may not attempt to access UniPostings systems, tools, or accounts without authorization.</p>
            <p>If UniPostings later introduces user accounts, you agree to:</p>
            <ul>
              <li>Provide accurate information.</li>
              <li>Maintain the security of your credentials.</li>
              <li>Accept responsibility for activity that occurs under your account.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="terms-section">
            <h2>4. Acceptable Use Policy</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Impersonate another person or misrepresent your affiliation.</li>
              <li>Post or submit false, misleading, or harmful content.</li>
              <li>Use the Service to harass, threaten, or exploit others.</li>
              <li>Upload malware or engage in activity that disrupts or damages the platform.</li>
              <li>Attempt to scrape or collect user data without consent.</li>
            </ul>
            <p className="terms-highlight">
              UniPostings reserves the right to remove any content or suspend accounts that violate these Terms.
            </p>
          </section>

          {/* Section 5 */}
          <section className="terms-section">
            <h2>5. Intellectual Property</h2>
            <p>
              All logos, trademarks, visual designs, and other materials created by UniPostings are owned by us and protected under applicable copyright and trademark laws.
            </p>
            <p>You may not use our branding, logo, or name without written consent.</p>
            <p>
              You retain ownership of your own submitted content but grant UniPostings permission (as outlined above) to display and share it across our services.
            </p>
          </section>

          {/* Section 6 */}
          <section className="terms-section">
            <h2>6. Third-Party Links</h2>
            <p>
              Our platform may contain links to third-party websites or services (such as Instagram, Stripe, or Google Forms).
            </p>
            <p>We are not responsible for the content, policies, or practices of any third-party site.</p>
          </section>

          {/* Section 7 */}
          <section className="terms-section">
            <h2>7. Disclaimer of Warranties</h2>
            <p>The Service is provided "as is" and "as available."</p>
            <p>We make no warranties, express or implied, including but not limited to:</p>
            <ul>
              <li>The accuracy or reliability of posted content.</li>
              <li>Continuous availability or error-free operation.</li>
              <li>Fitness for any particular purpose.</li>
            </ul>
            <p className="terms-note">
              UniPostings does not guarantee that connections made through the platform (e.g., roommates or friendships) will result in compatible or safe relationships. Users should exercise discretion when meeting others.
            </p>
          </section>

          {/* Section 8 */}
          <section className="terms-section">
            <h2>8. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, UniPostings, its founders, team members, and affiliates will not be liable for:</p>
            <ul>
              <li>Any indirect, incidental, consequential, or punitive damages arising from your use of the Service.</li>
              <li>Any harm, data loss, or disputes between users.</li>
            </ul>
            <p className="terms-highlight">
              Your sole remedy for dissatisfaction with the Service is to stop using it.
            </p>
          </section>

          {/* Section 9 */}
          <section className="terms-section">
            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless UniPostings, its affiliates, and personnel from any claims, damages, or expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service,</li>
              <li>Your submitted content, or</li>
              <li>Your violation of these Terms.</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="terms-section">
            <h2>10. Termination</h2>
            <p>
              We reserve the right to suspend or terminate access to the Service at our discretion, with or without notice, if we believe you have violated these Terms or acted unlawfully.
            </p>
          </section>

          {/* Section 11 */}
          <section className="terms-section">
            <h2>11. Changes to These Terms</h2>
            <p>
              We may modify these Terms at any time. Updates will be posted on this page with the revised "Last Updated" date. Continued use after updates means you accept the new Terms.
            </p>
          </section>

          {/* Section 12 */}
          <section className="terms-section">
            <h2>12. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the Commonwealth of Pennsylvania, without regard to its conflict of law principles.
            </p>
          </section>

          {/* Section 13 */}
          <section className="terms-section">
            <h2>13. Contact Us</h2>
            <p>For questions or concerns about these Terms, contact us at:</p>
            <div className="contact-info">
              <p><strong>UniPostings Legal Team</strong></p>
              <p>📧 <a href="mailto:hello@unipostings.com" className="terms-link">hello@unipostings.com</a></p>
              <p>🌐 <a href="https://www.unipostings.com" className="terms-link" target="_blank" rel="noopener noreferrer">www.unipostings.com</a></p>
            </div>
          </section>

          {/* Back to Home Button */}
          <div className="terms-footer-cta">
            <Button 
              label="Back to Home" 
              className="terms-cta-button"
              onClick={() => navigate('/')}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Contact Us</h4>
              <p>hello@UniPostings.com</p>
              <p>Questions? We're here to help!</p>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <p><a href="/privacy-policy" className="footer-link">Privacy Policy</a></p>
              <p><a href="/terms-of-service" className="footer-link">Terms of Service</a></p>
            </div>
            <div className="footer-section">
              <h4>Follow Us</h4>
              <p>@UniPostings on Instagram</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 UniPostings LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;

